/* eslint-disable max-len */
/* eslint-disable no-console */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Grid, Checkbox, Radio, IconButton, Typography, CircularProgress, Box, Tooltip } from '@mui/material';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getCategories, getSubcategories } from '../../../../../../../containers/actions/resource_configuration';
import { useSelector } from 'react-redux';
import useResourceHelper from './ContentCards/useResourceHelper';

// Interface for category data structure
interface CategoryNode {
	_id: string;
	name: string;
	level: number;
	isSubcategory?: boolean;
	children?: CategoryNode[];
	parentCategoryId?: string;
	hasLoadedChildren?: boolean;
	isLoading?: boolean;
	hasError?: boolean;
	hasSubcategories?: boolean;
}

interface TreeViewProps {
	articleCreate?: string;
	selectionType?: 'checkbox' | 'radio';
	onSelect?: (selectedId: string | string[], selectedData?: any) => void;
	maxDepth?: number;
	initialSelectedId?: string | string[];
	refreshTrigger?: number;
	excludeCategoryId?: string; // Category ID to exclude from the tree (e.g., current category being moved)
	searchTerm?: string; // NEW: Add search term prop
	disableUncategorized?: boolean; // NEW: Add prop to disable uncategorized category selection
	isSubcategory?: boolean;
}

const TreeView: React.FC<TreeViewProps> = (props) =>
{
	const [ selected, setSelected ] = useState<Record<string, boolean>>({});
	const [ expanded, setExpanded ] = useState<Record<string, boolean>>({});
	const { articleCreate, selectionType = 'checkbox', onSelect, maxDepth = 5, initialSelectedId, refreshTrigger = 0, excludeCategoryId, searchTerm = '', disableUncategorized = false, isSubcategory = false } = props;
	const [ selectedRadio, setSelectedRadio ] = useState<string | null>(
		Array.isArray(initialSelectedId) ? initialSelectedId[0] : initialSelectedId || null
	);
	const [ treeData, setTreeData ] = useState<CategoryNode[]>([]);
	const [ filteredTreeData, setFilteredTreeData ] = useState<CategoryNode[]>([]);
	const [ isLoading, setIsLoading ] = useState<boolean>(true);
	const [ error, setError ] = useState<string | null>(null);
	const categoriesLoaded = useRef(false);

	console.log('**isdisplayingenabledcategories**', Boolean(initialSelectedId));

	// Get company ID from Redux state
	const companyId = useSelector((state: any) => state.mysession.session?.companyId);
	const { isAdminView } = useResourceHelper();
	// Filter tree data based on search term - Simple approach like doctor filter
	const filterTreeData = useCallback((data: CategoryNode[], searchTerm: string): CategoryNode[] =>
	{
		if (!searchTerm.trim())
		{
			return data; // Return all data if no search term
		}

		const searchLower = searchTerm.toLowerCase();

		// Step 1: Flatten the tree to get all categories
		const flattenTree = (nodes: CategoryNode[]): CategoryNode[] =>
		{
			let allNodes: CategoryNode[] = [];

			nodes.forEach((node) =>
			{
				allNodes.push(node);
				if (node.children && node.children.length > 0)
				{
					allNodes = allNodes.concat(flattenTree(node.children));
				}
			});

			return allNodes;
		};

		// Step 2: Filter like your doctor example - simple and clean!
		const allCategories = flattenTree(data);
		const matchingCategories = allCategories.filter((category) =>
			category.name.toLowerCase().includes(searchLower)
		);

		// Step 3: Get unique IDs of matching categories and their parents
		const matchingIds = new Set<string>();

		matchingCategories.forEach((category) =>
		{
			matchingIds.add(category._id);
			// Add parent IDs to maintain tree structure
			if (category.parentCategoryId)
			{
				matchingIds.add(category.parentCategoryId);
			}
		});

		// Step 4: Reconstruct tree with only matching nodes
		const reconstructTree = (nodes: CategoryNode[]): CategoryNode[] =>
		{
			return nodes
				.filter((node) => matchingIds.has(node._id))
				.map((node) => ({
					...node,
					children : node.children ? reconstructTree(node.children) : []
				}));
		};

		return reconstructTree(data);
	}, []);

	// Extract fetchRootCategories function to be reusable
	const fetchRootCategories = useCallback(async (forceRefresh = false) =>
	{
		const from = isAdminView ? 'Configuration' : 'MyResource';

		if (!companyId)
		{
			setError('Company ID not available');
			setIsLoading(false);

			return;
		}

		// Skip if categories have already been loaded and we're not forcing a refresh
		if (categoriesLoaded.current && !forceRefresh)
		{
			setIsLoading(false);

			return;
		}

		try
		{
			setIsLoading(true);
			// Fetch root categories (parentCategoryId=null for root level)

			const query: any = { parentCategoryId: 'null', level: 0, from };

			if (initialSelectedId)
			{
				query.isEnabled = true;
			}

			const response = await getCategories(companyId, query);

			// Transform data to match the expected format for TreeView
			// Filter for actual root categories (parentCategoryId should be null/undefined) AND exclude the specified category
			const formattedData = response.categories
				.filter((category: any) =>
				{
					// First check if it's actually a root category
					const isRootCategory = !category.parentCategoryId || category.parentCategoryId === null;

					// Then check if it should be excluded
					const shouldExclude = excludeCategoryId && category._id === excludeCategoryId;

					return isRootCategory && !shouldExclude;
				})
				.map((category: any) => ({
					_id               : category._id,
					name              : category.name,
					level             : category.level || 0,
					isSubcategory     : category.isSubcategory || false,
					children          : [],
					hasLoadedChildren : false,
					isLoading         : false,
					hasSubcategories  : category.subcategoryCount > 0
				}));

			setTreeData(formattedData);
			categoriesLoaded.current = true;
			setError(null);
		}
		catch (err: any)
		{
			console.error('Error fetching categories:', err);
			setError(err.message || 'Failed to load categories');
		}
		finally
		{
			setIsLoading(false);
		}
	}, [ companyId, excludeCategoryId ]);

	// Effect to initialize selected items from initialSelectedId
	useEffect(() =>
	{
		// Skip if no initialSelectedId
		if (!initialSelectedId) return;

		if (selectionType === 'radio')
		{
			// For radio, just use the first item if it's an array
			const radioId = Array.isArray(initialSelectedId) ? initialSelectedId[0] : initialSelectedId;

			setSelectedRadio(radioId);
		}
		else
		{
			// For checkbox, create a record for all initialSelectedIds
			const initialSelections: Record<string, boolean> = {};
			const ids = Array.isArray(initialSelectedId) ? initialSelectedId : [ initialSelectedId ];

			ids.forEach((id) =>
			{
				if (id) initialSelections[id] = true;
			});

			setSelected(initialSelections);
		}
	}, []);

	// Effect to watch for refreshTrigger changes and reload categories
	useEffect(() =>
	{
		// Skip the initial render
		if (refreshTrigger > 0)
		{
			// Force a refresh regardless of the categoriesLoaded flag
			fetchRootCategories(true);
		}
	}, [ refreshTrigger, fetchRootCategories ]);

	// Fetch root level categories on component mount
	useEffect(() =>
	{
		fetchRootCategories();
	}, [ fetchRootCategories ]);

	// Effect to filter tree data based on search term
	useEffect(() =>
	{
		if (treeData.length > 0)
		{
			const filtered = filterTreeData(treeData, searchTerm);

			setFilteredTreeData(filtered);
		}
	}, [ treeData, searchTerm, filterTreeData ]);

	// Function to fetch subcategories when a node is expanded
	const fetchSubcategories = async (categoryId: string, level: number, nodeIndex: number[], currentData: CategoryNode[]) =>
	{
		const from = isAdminView ? 'Configuration' : 'MyResource';

		try
		{
			// Find the node to update in a copy of the data
			const updatedData = [ ...currentData ];
			let currentNode = updatedData;
			let targetNode: any = null;

			for (let i = 0; i < nodeIndex.length; i++)
			{
				const index = nodeIndex[i];

				if (i === nodeIndex.length - 1)
				{
					targetNode = currentNode[index];
				}
				else
				{
					currentNode = currentNode[index].children || [];
				}
			}

			// Set loading state
			if (targetNode)
			{
				targetNode.isLoading = true;
				setTreeData(updatedData);
			}

			const query: any = { from };

			if (initialSelectedId)
			{
				query.isEnabled = true;
			}

			// Fetch subcategories for the given parent
			const subcategories = await getSubcategories(categoryId, query);

			// Format the subcategories data
			const formattedSubcategories = subcategories
				.filter((subcategory: any) =>
				{
					const shouldExclude = excludeCategoryId && subcategory._id === excludeCategoryId;

					return !shouldExclude;
				}) // Filter out excluded category
				.map((subcategory: any) => ({
					_id               : subcategory._id,
					name              : subcategory.name,
					level             : subcategory.level || level + 1,
					isSubcategory     : true,
					parentCategoryId  : categoryId,
					children          : [],
					hasLoadedChildren : false,
					hasSubcategories  : subcategory.subcategoryCount > 0
				}));

			// Create a new copy of the tree data to avoid mutation
			const newUpdatedData = [ ...updatedData ];

			// Find the node to update again (to account for any state changes during fetch)
			currentNode = newUpdatedData;
			targetNode = null;

			for (let i = 0; i < nodeIndex.length; i++)
			{
				const index = nodeIndex[i];

				if (i === nodeIndex.length - 1)
				{
					targetNode = currentNode[index];
				}
				else
				{
					currentNode = currentNode[index].children || [];
				}
			}

			if (targetNode)
			{
				targetNode.children = formattedSubcategories;
				targetNode.hasLoadedChildren = true;
				targetNode.isLoading = false;
				targetNode.hasSubcategories = formattedSubcategories.length > 0;
				setTreeData(newUpdatedData);
			}

			return formattedSubcategories.length > 0;
		}
		catch (err: any)
		{
			console.error('Error fetching subcategories:', err);

			// Update the node to indicate error
			const updatedData = [ ...currentData ];
			let currentNode = updatedData;
			let targetNode: any = null;

			for (let i = 0; i < nodeIndex.length; i++)
			{
				const index = nodeIndex[i];

				if (i === nodeIndex.length - 1)
				{
					targetNode = currentNode[index];
				}
				else
				{
					currentNode = currentNode[index].children || [];
				}
			}

			if (targetNode)
			{
				targetNode.isLoading = false;
				targetNode.hasLoadedChildren = true;
				targetNode.hasError = true;
				setTreeData(updatedData);
			}

			return false;
		}
	};

	// Handle selection of nodes
	const toggleSelection = (id: string, level: number, node: CategoryNode) =>
	{
		// Check if max depth is reached - show toast but allow selection
		if (maxDepth !== undefined && level >= maxDepth - 1 && selectionType === 'radio' && isSubcategory)
		{
			toast.error(`Maximum depth limit reached. Cannot create subcategory under "${node.name}".`, {
				position  : toast.POSITION.TOP_RIGHT,
				className : 'toast-message'
			});

			return;
		}

		// For radio buttons, prevent deselection of initialSelectedId
		if (selectionType === 'radio')
		{
			// Don't allow deselection if this is initialSelectedId (clicking already selected item)
			if (id === initialSelectedId && id === selectedRadio)
			{
				return; // Prevent deselection of initialSelectedId
			}

			setSelectedRadio(id);
			if (onSelect && typeof onSelect === 'function')
			{
				onSelect(id, node); // Pass the entire node data
			}
		}
		else
		{
			// For checkboxes, allow selection and deselection regardless of initialSelectedId
			// Remove the restriction on deselecting initialSelectedId items
			// Use prevState to ensure we're working with the most current state
			setSelected((prev) =>
			{
				const newSelected = { ...prev, [id]: !prev[id] };

				const wasPreviouslySelected = Boolean(prev[id]);
				// Call onSelect INSIDE the setState callback to ensure we're using the latest state

				if (onSelect && typeof onSelect === 'function' && !wasPreviouslySelected)
				{
					const selectedIds = Object.keys(newSelected).filter((key) => newSelected[key]);

					setTimeout(() => onSelect(selectedIds), 0);
				}

				return newSelected;
			});
		}
	};

	// Helper function to find a node by its index path
	const findNodeByIndex = (data: CategoryNode[], indices: number[]): CategoryNode | null =>
	{
		if (indices.length === 0 || !data || data.length === 0) return null;

		let current: CategoryNode | null = data[indices[0]];

		for (let i = 1; i < indices.length; i++)
		{
			if (!current || !current.children) return null;
			current = current.children[indices[i]];
			if (!current) return null;
		}

		return current;
	};

	// Handle expanding of nodes
	const toggleExpand = async (id: string, level: number, nodeIndex: number[]) =>
	{
		const isExpanding = !expanded[id];

		// If we're expanding and haven't loaded children yet, fetch them
		const currentNode = findNodeByIndex(treeData, nodeIndex);

		if (isExpanding && currentNode && !currentNode.hasLoadedChildren)
		{
			await fetchSubcategories(id, level, nodeIndex, treeData);
		}

		setExpanded((prev) => ({ ...prev, [id]: isExpanding }));
	};
	const uncategorizedId = useSelector((state: any) => state.myresource?.uncategorizedId);

	// Recursive function to render the tree
	const renderTree = (node: CategoryNode, level = 0, nodeIndex: number[] = []) =>
	{
		const hasChildren = node.children && node.children.length > 0;
		const isMaxDepth = maxDepth !== undefined && level >= maxDepth - 1;
		const isUncategorized = node.name.toLowerCase() === 'uncategorized' || node._id === uncategorizedId;
		const shouldDisableUncategorized = disableUncategorized && isUncategorized;

		// Check if this node is in initialSelectedId (for radio mode only now)
		const isInInitialSelected = Array.isArray(initialSelectedId)
			? initialSelectedId.includes(node._id)
			: node._id === initialSelectedId;

		// Remove isSelectedAndDisabled - we want to allow unchecking
		// Only keep for radio buttons
		const isSelectedAndDisabled = selectionType === 'radio' && isInInitialSelected && selectedRadio === node._id;

		// Determine if expand icon should be enabled based on subcategory presence
		// Don't disable expand for max depth - allow expansion but show toast on selection
		const hasSubcategories = node.hasSubcategories || hasChildren;
		const expandDisabled = !hasSubcategories;
		// Only disable for uncategorized, not for max depth
		const nodeDisabled = shouldDisableUncategorized;

		return (
			<Grid container direction='column' key={node._id}>
				<Grid container alignItems='center' style={{ paddingLeft: `${level * 20}px` }}>
					<Grid item>
						<IconButton
							size='small'
							onClick={() => toggleExpand(node._id, level, nodeIndex)}
							disabled={expandDisabled}
							sx={{
								opacity : hasSubcategories ? 1 : 0.5
							}}
						>
							{expanded[node._id] ? <ExpandMore /> : <ChevronRight />}
						</IconButton>
					</Grid>
					<Grid item>
						{(selectionType === 'checkbox')
							? (
								<Checkbox
									checked={selected[node._id] || false}
									onChange={() => toggleSelection(node._id, level, node)}
									disabled={nodeDisabled} // Use combined disabled state
									sx={{
										'&.Mui-checked' : {
											color : '#24A4C4'
										}
									}}
								/>
							)
							: (
								<Box
									onClick={() =>
									{
										if (!nodeDisabled)
										{
											toggleSelection(node._id, level, node);
										}
									}}
									sx={{
										cursor     : nodeDisabled ? 'not-allowed' : 'pointer',
										display    : 'flex',
										alignItems : 'center'
									}}
								>
									<Radio
										checked={selectedRadio === node._id}
										disabled={nodeDisabled} // Use combined disabled state
										sx={{ pointerEvents: 'none' }} // Prevent radio from capturing click
									/>
								</Box>
							)}
					</Grid>
					<Grid item sx={{ minWidth: 0, flex: 1 }}>
						<Tooltip title={shouldDisableUncategorized ? 'Cannot create subcategory under Uncategorized' : node.name} placement='top' arrow>
							<Typography
								variant='body1'
								sx={{
									color : isSelectedAndDisabled
										? 'text.disabled'
										: selectedRadio === node._id
											? '#24A4C4'
											: nodeDisabled ? 'text.disabled' : 'text.primary',
									fontWeight    : selectedRadio === node._id ? 500 : 400,
									fontStyle     : nodeDisabled ? 'italic' : 'normal',
									cursor        : nodeDisabled ? 'not-allowed' : 'pointer',
									maxWidth      : 180, // Adjust as needed
									overflow      : 'hidden',
									textOverflow  : 'ellipsis',
									whiteSpace    : 'nowrap',
									display       : 'inline-block',
									verticalAlign : 'middle'
								}}
								onClick={() =>
								{
									if (!nodeDisabled)
									{
										toggleSelection(node._id, level, node);
									}
								}}
							>
								{node.name}
								{isSubcategory && isMaxDepth && selectionType === 'radio' &&
									<Typography component='span' variant='caption' sx={{ ml: 1, color: 'warning.main' }}>
										(max depth reached)
									</Typography>
								}
								{shouldDisableUncategorized &&
									<Typography component='span' variant='caption' sx={{ ml: 1, color: 'text.disabled' }}>
										(not allowed)
									</Typography>
								}
								{selectionType === 'radio' && isSelectedAndDisabled &&
									<Typography component='span' variant='caption' sx={{ ml: 1, color: 'text.disabled' }}>
										(already selected)
									</Typography>
								}
							</Typography>
						</Tooltip>
					</Grid>
				</Grid>

				{expanded[node._id] && (
					<Grid container direction='column'>
						{node.isLoading
							? (
							// Loading state
								<Box pl={15} py={1} display='flex' alignItems='center'>
									<CircularProgress size={20} />
									<Typography variant='body2' ml={1} color='text.secondary'>
										Loading subcategories...
									</Typography>
								</Box>
							)
							: hasChildren
								? (
							// Has subcategories
									node?.children?.map((child, index) => renderTree(child, level + 1, [ ...nodeIndex, index ]))
								)
								: null}
					</Grid>
				)}
			</Grid>
		);
	};

	if (isLoading)
	{
		return (
			<Box display='flex' justifyContent='center' alignItems='center' p={3}>
				<CircularProgress size={24} />
				<Typography variant='body2' ml={1.5} color='text.secondary'>
					Loading categories...
				</Typography>
			</Box>
		);
	}

	if (error)
	{
		return (
			<Box p={2}>
				<Typography variant='body2' color='error'>Error: {error}</Typography>
			</Box>
		);
	}

	return (
		<Grid container direction='column' sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
			{filteredTreeData.length > 0
				? (
					<Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
						{filteredTreeData.map((node, index) => renderTree(node, 0, [ index ]))}
					</Box>
				)
				: (
					<Box p={2} display='flex' alignItems='center' justifyContent='center'>
						<Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic' }}>
							{searchTerm.trim() ? 'No categories match your search' : 'No categories found'}
						</Typography>
					</Box>
				)}
		</Grid>
	);
};

export default TreeView;
